/* filepath: /Users/johnmcgurk/Documents/GitHub/preservoir-web-client/src/components/layout/Header.tsx */
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import switcherIcon from '@assets/header/switcher-icon.svg';
import personIcon from '@assets/header/person.svg';
import { useProfile } from '@contexts/profile/ProfileContext';
import { useBusiness } from '@contexts/business/BusinessContext';
import { useLayoutNavigation } from '@hooks/useLayoutNavigation';
import { formatCurrency } from '@utils/formatters';
import { Button, Modal } from '@components/ui';
import { useModal } from '@hooks/useModal';
import BusinessOverviewModal from '@components/modals/BusinessOverviewModal';
import ProfileModal from '@components/modals/ProfileModal';
import styles from './Header.module.css'; // ✅ Import CSS module

const BACK_TARGETS: Record<string, { fallback: string; label: string; useHistory: boolean }> = {
  Transactions: { fallback: '/wallet', label: 'Go back to wallet', useHistory: false },
  // Reached from Manage assets, the purchase page and email deep links, so back returns to wherever
  // the client came from; a deep link has no in-app history and falls back to Manage assets.
  'Auto-roll': { fallback: '/manage', label: 'Go back', useHistory: true },
};

interface HeaderProps {
  title: string;
  username?: string;
  onTriggerExit?: (callback: () => void) => void;
}

const Header: React.FC<HeaderProps> = ({ title, onTriggerExit: _onTriggerExit }) => {
  const { profileData, clearAndLogout, clearCurrentBusiness } = useProfile();
  const { businessProfile } = useBusiness();
  const { navigateToLogoOnlyRoute } = useLayoutNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = BACK_TARGETS[title];

  // Modal state management with new hooks
  const businessModal = useModal();
  const profileModal = useModal();
  const [businessButtonRef, setBusinessButtonRef] = useState<HTMLButtonElement | null>(null);
  const [profileButtonRef, setProfileButtonRef] = useState<HTMLButtonElement | null>(null);

  // Calculate modal positions based on button positions
  const calculateModalPosition = (buttonRef: HTMLButtonElement | null) => {
    if (!buttonRef) return { top: 0, right: 0 };
    const rect = buttonRef.getBoundingClientRect();
    return {
      top: rect.bottom + 10, // 10px below button
      right: window.innerWidth - rect.right, // Right-align to button
    };
  };

  const handleProfileClick = () => {
    // Clear business immediately, then start animation
    clearCurrentBusiness();

    // Start animation after a tiny delay to allow state update
    setTimeout(() => {
      navigateToLogoOnlyRoute('/business-select', {
        state: { from: '/dashboard' },
        showGradient: false,
        fadeToColor: 'var(--surface-auth)',
      });
    }, 10);
  };

  const handleLogout = () => {
    // Use navigateToLogoOnlyRoute for smooth transition back to login
    navigateToLogoOnlyRoute('/login', {
      replace: true,
      fadeToColor: 'var(--surface-auth)',
      showGradient: false, // Hide gradient during logout
    });

    // Clear authentication after a short delay to allow the navigation animation to start
    setTimeout(() => {
      clearAndLogout();
    }, 200);
  };

  const handleBackClick = () => {
    if (!backTarget) return;
    if (backTarget.useHistory && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(backTarget.fallback);
    }
  };

  return (
    <header className={styles.header}>
      {' '}
      {/* ✅ Use CSS module classes */}
      <div className={styles.headerLeft}>
        {backTarget && (
          <Button
            variant="text"
            nativeFont
            className={styles.back}
            onClick={handleBackClick}
            aria-label={backTarget.label}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </Button>
        )}
        <h1 className={styles.headerTitle}>{title}</h1>
      </div>
      <div className={styles.headerActions}>
        {/* Business Overview Button */}
        <Button
          ref={setBusinessButtonRef}
          variant="ghost"
          className={`${styles.businessSwitcher} ${businessModal.isOpen ? styles.active : ''}`}
          onClick={() => {
            profileModal.closeModal(); // Close profile modal if open
            businessModal.openModal();
          }}
        >
          <div className={styles.businessContent}>
            {/* #160b — display GROSS ledger cash, not the net spendable figure.
                A net pill appears to shrink daily as fees accrue with no money
                movement. This pill gates nothing, so there is no cap to protect. */}
            <span className={styles.businessBalance}>
              {formatCurrency(businessProfile?.virtualCashAccount?.grossAvailableBalance || 0)}
            </span>
            <span className={styles.businessName}>{businessProfile?.name || 'Business Name'}</span>
          </div>
          <img src={switcherIcon} alt="Switcher" className={styles.switcherIcon} />
        </Button>

        {/* Profile Button */}
        <Button
          ref={setProfileButtonRef}
          variant="ghost"
          nativeFont
          className={`${styles.profile} ${profileModal.isOpen ? styles.active : ''}`}
          onClick={() => {
            businessModal.closeModal(); // Close business modal if open
            profileModal.openModal();
          }}
        >
          <img src={personIcon} alt="Profile" className={styles.personIcon} />
        </Button>
      </div>
      {/* Modals with new Modal component */}
      <Modal
        isOpen={businessModal.isOpen}
        onClose={businessModal.closeWithAnimation}
        position={calculateModalPosition(businessButtonRef)}
      >
        <BusinessOverviewModal
          isClosing={businessModal.isClosing}
          onClose={businessModal.closeWithAnimation}
          onSwitchBusiness={handleProfileClick} // Use existing logic
          businessName={businessProfile?.name || 'Business Name'}
        />
      </Modal>
      <Modal
        isOpen={profileModal.isOpen}
        onClose={profileModal.closeWithAnimation}
        position={calculateModalPosition(profileButtonRef)}
      >
        <ProfileModal
          isClosing={profileModal.isClosing}
          onClose={profileModal.closeWithAnimation}
          onLogout={handleLogout} // Use existing logic
          userName={`${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim()}
          userEmail={profileData?.email || ''}
        />
      </Modal>
    </header>
  );
};

export default Header;
